(() => {
  const number = value => {
    const fractions = {'¼':.25,'½':.5,'¾':.75,'⅛':.125,'⅜':.375,'⅝':.625,'⅞':.875,'⅘':.8};
    const text = String(value).trim();
    const fraction = fractions[text.slice(-1)];
    if (fraction !== undefined) return Number(text.slice(0,-1) || 0) + fraction;
    return /^\d+(\.\d+)?$/.test(text) ? Number(text) : NaN;
  };
  const parse = (table, values) => {
    if (!table) return [];
    const headers = [...table.querySelectorAll('thead th')].map(x => x.textContent.trim().toLowerCase());
    const keys = headers.map(x => /chest width/.test(x) ? 'width' : /body length/.test(x) ? 'length' : /waist range/.test(x) ? 'range' : /waistband/.test(x) ? 'waist' : /inseam/.test(x) ? 'inseam' : /outseam/.test(x) ? 'outseam' : /sleeve/.test(x) ? 'sleeve' : null);
    return [...table.querySelectorAll('tbody tr')].map(tr => {
      const cells = [...tr.querySelectorAll('th,td')].map(x => x.textContent.trim());
      const row = {size:cells[0]};
      cells.forEach((value,i) => { if (keys[i]) row[keys[i]] = keys[i] === 'range' ? value.split(/[–—-]/).map(number) : number(value); });
      return row;
    }).filter(row => values.includes(row.size));
  };
  const recommend = (rows, kind, input) => {
    const {height,weight,fit,waist,inseam,chest} = input;
    const fitIndex = ['fitted','oversized'].indexOf(fit);
    if (!Number.isFinite(height) || !Number.isFinite(weight) || height < 58 || height > 82 || weight < 85 || weight > 350 || fitIndex < 0) return {error:'range_error'};
    const bottom = ['pants','shorts'].includes(kind);
    if (!rows.length) return {error:'chart_error'};
    if (bottom) {
      if (!Number.isFinite(waist) || waist < 20 || waist > 60) return {error:'waist_error'};
      if (Number.isFinite(inseam) && (inseam < 20 || inseam > 42)) return {error:'inseam_error'};
      let candidates;
      if (kind === 'pants') {
        candidates = rows.filter(r => r.range?.length === 2 && r.range.every(Number.isFinite) && waist >= r.range[0] && waist <= r.range[1] - 1);
        candidates = candidates.map(r => ({...r,score:Math.abs(waist - (r.range[0] + [4,2][fitIndex])) + (inseam ? Math.abs(r.inseam-inseam)*2 : 0)}));
      } else {
        candidates = rows.filter(r => Number.isFinite(r.waist) && r.waist >= waist+.5 && r.waist <= waist+4).map(r => ({...r,score:Math.abs(r.waist-waist-[1,2.5][fitIndex])}));
      }
      if (!candidates.length) return {error:'no_match'};
      candidates.sort((a,b) => a.score-b.score);
      const best = candidates[0];
      const alternative = candidates.find(r => r.size !== best.size);
      return {best,alternative,bottom:true,lengthWarning:kind === 'pants' && !!inseam && best.inseam < inseam-.5, measured:true};
    }
    if (Number.isFinite(chest) && (chest < 25 || chest > 65)) return {error:'chest_error'};
    const bodyChest = chest || Math.sqrt(weight/height)*25;
    const outer = ['hoodie','zip','explore_zip'].includes(kind);
    const ease = (outer ? [6,12] : [2,8])[fitIndex];
    const fittedWidth = (bodyChest + (outer ? 6 : 2))/2;
    const oversizedWidth = (bodyChest + (outer ? 12 : 8))/2;
    const targetWidth = (bodyChest + ease)/2;
    const targetLength = height*[.387,.410][fitIndex];
    const candidates = rows.filter(r => Number.isFinite(r.width) && Number.isFinite(r.length) && r.width*2 >= bodyChest+1);
    if (!candidates.length || targetLength > Math.max(...candidates.map(r=>r.length))+3 || oversizedWidth < Math.min(...candidates.map(r=>r.width))-3) return {error:'no_match'};
    const ranked = candidates.map(r => ({...r,score:Math.abs(r.width-targetWidth)/1.5 + Math.max(0,targetLength-r.length)*3 + Math.max(0,r.length-targetLength)*.5})).sort((a,b)=>Math.round(a.score*1e9)-Math.round(b.score*1e9) || a.width-b.width || a.length-b.length);
    const best = ranked[0];
    const smallestWidth = Math.min(...rows.filter(r=>Number.isFinite(r.width)).map(r=>r.width));
    const smallestRoomy = best.width === smallestWidth && smallestWidth > fittedWidth+2;
    const index = rows.findIndex(r=>r.size===best.size);
    const adjacent = fit === 'fitted' ? rows[index+1] || rows[index-1] : rows[index-1] || rows[index+1];
    const alternative = !smallestRoomy && adjacent && adjacent.width*2 >= bodyChest+1 ? adjacent : undefined;
    return {best,alternative,smallestRoomy,bottom:false,lengthWarning:height>=74 || best.length < targetLength-1, fitLimited:fit === 'oversized' && best.width < targetWidth-1 ? 'oversized_limit' : fit === 'fitted' && best.width > targetWidth+2 ? 'fitted_limit' : null, measured:!!chest};
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = {number,parse,recommend};
  if (typeof customElements === 'undefined' || customElements.get('abstract-size-finder')) return;
  class SizeFinder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      try {
        this.data = JSON.parse(this.querySelector('[data-asf-config]').textContent);
        this.strings = this.data.strings;
        this.rows = parse(this.querySelector('template').content.querySelector('table'), this.data.values);
        const bottom = ['pants','shorts'].includes(this.data.kind);
        const valid = this.rows.length > 1 && this.rows.length === this.data.values.length && new Set(this.rows.map(r => r.size)).size === this.rows.length && this.rows.every(r => bottom ? Number.isFinite(r.inseam) && (this.data.kind === 'pants' ? r.range?.length === 2 && r.range.every(Number.isFinite) : Number.isFinite(r.waist)) : Number.isFinite(r.width) && Number.isFinite(r.length));
        if (!valid) return;
        this.ready = true;
        this.hidden = false;
        this.addEventListener('click', event => this.click(event));
        this.addEventListener('input', () => this.clearResult());
        this.addEventListener('change', () => this.clearResult());
        this.addEventListener('keydown', event => { if (event.key === 'Enter' && event.target.matches('input')) { event.preventDefault(); this.calculate(); } });
        this.querySelectorAll('[data-bottom]').forEach(el => {el.hidden = !bottom;});
        this.querySelector('[data-chest]').hidden = bottom;
        this.querySelector('[data-inseam]').hidden = this.data.kind !== 'pants';
        this.unit = 'imperial';
        this.fit = 'oversized';
      } catch (_) { this.hidden = true; }
    }
    t(key, values={}) { return Object.entries(values).reduce((text,[k,v])=>text.replaceAll('{'+k+'}',v),this.strings[key] || key); }
    clearResult() { this.querySelector('[data-results]').hidden=true; this.querySelector('[data-error]').textContent=''; this.querySelector('[data-summary]').textContent=this.t('hint'); }
    click(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.dataset.unit) {
        if (button.dataset.unit === this.unit) return;
        const current = this.read();
        this.unit = button.dataset.unit;
        this.querySelectorAll('[data-unit]').forEach(b => b.setAttribute('aria-pressed',b===button));
        const metric=this.unit==='metric';
        this.querySelector('[data-imperial]').hidden=metric;
        this.querySelector('[data-metric]').hidden=!metric;
        this.querySelector('[data-weight-unit]').textContent=metric?'kg':'lb';
        this.querySelectorAll('[data-length-unit]').forEach(el=>el.textContent=metric?'cm':'in');
        const set=(name,value)=>{this.querySelector('[name="'+name+'"]').value=value;};
        if (Number.isFinite(current.height)) {set('cm',+(current.height*2.54).toFixed(1));set('feet',Math.floor(current.height/12));set('inches',+(current.height%12).toFixed(2));}
        if (Number.isFinite(current.weight)) set('weight',+(current.weight*(metric?.45359237:1)).toFixed(2));
        ['waist','inseam','chest'].forEach(n=>{if (current[n]) set(n,+(current[n]*(metric?2.54:1)).toFixed(2));});
        this.clearResult();
      }
      if (button.dataset.fit) {this.fit=button.dataset.fit;this.querySelectorAll('[data-fit]').forEach(b=>b.setAttribute('aria-pressed',b===button));this.clearResult();}
      if (button.hasAttribute('data-calculate')) this.calculate();
      if (button.dataset.choose) this.choose(button.dataset.choose);
      if (button.hasAttribute('data-chart')) {
        const scope = this.closest('.shopify-section') || document;
        const guide=scope.querySelector('.asg details');
        if (guide) {guide.open=true;const chart=guide.querySelector('.asg-table-wrap') || guide;chart.scrollIntoView({behavior:'auto',block:'center'});}
      }
    }
    read() {
      const n = name => {const v=this.querySelector('[name="'+name+'"]').value.trim();return v===''?NaN:Number(v);};
      const metric=this.unit==='metric';
      const ft=n('feet'), inches=n('inches');
      const height=metric?n('cm')/2.54:(Number.isInteger(ft) && inches>=0 && inches<12 ? ft*12+inches : NaN);
      return {height,weight:n('weight')/(metric?.45359237:1),fit:this.fit,waist:n('waist')/(metric?2.54:1),inseam:n('inseam')/(metric?2.54:1),chest:n('chest')/(metric?2.54:1)};
    }
    available(size) {
      const scope=this.closest('.shopify-section') || document;
      const selector=scope.querySelector('variant-selector');
      const selected=selector?[...selector.querySelectorAll('fieldset')].map(f=>f.querySelector('input:checked')?.value || f.querySelector('select')?.value):[];
      return this.data.variants.some(v=>v.available && v.options[this.data.position-1]===size && v.options.every((value,i)=>i===this.data.position-1 || !selected[i] || value===selected[i]));
    }
    measure(value) { return Number((value*(this.unit==='metric'?2.54:1)).toFixed(2))+(this.unit==='metric'?' cm':'″'); }
    calculate() {
      const input=this.read();
      const result=recommend(this.rows,this.data.kind,input);
      const other=recommend(this.rows,this.data.kind,{...input,fit:this.fit==='fitted'?'oversized':'fitted'});
      const error=this.querySelector('[data-error]');
      const panel=this.querySelector('[data-results]');
      if (result.error) {panel.hidden=true;error.textContent=this.t(result.error);return;}
      error.textContent='';
      panel.replaceChildren();
      const make=(tag,cls,text)=>{const e=document.createElement(tag);e.className=cls;e.textContent=text;return e;};
      const head=make('div','asf-result-head','');
      head.append(make('span','asf-eyebrow',this.t('starting_size')),make('span','asf-estimate',this.t('estimate')));
      panel.append(head);
      if(result.smallestRoomy) {
        const note=make('div','asf-roomy-note','');
        note.append(make('strong','asf-choice-title',this.t('smallest_roomy_title')),make('p','',this.t('smallest_roomy_note',{size:result.best.size})));
        panel.append(note);
      }
      const card=(row,primary)=>{
        const el=make('div',primary?'asf-choice asf-choice-main':'asf-choice','');
        const size=make('strong','asf-size',row.size);
        const body=make('div','asf-choice-body','');
        let label=this.t(result.smallestRoomy?'smallest_size':result.fitLimited?'closest_match':this.fit);
        if (!primary) label=this.t(result.bottom?'alternative':row.width<result.best.width?'closer':'roomier');
        body.append(make('strong','asf-choice-title',label));
        const details=result.bottom ? (row.range?this.t('waist_range',{value:row.range.map(v=>this.measure(v)).join('–')}):this.t('waistband',{value:this.measure(row.waist)})) : this.t('dimensions',{width:this.measure(row.width),length:this.measure(row.length)});
        body.append(make('span','asf-measures',details));
        if (result.bottom) body.append(make('span','asf-measures',this.t('inseam_value',{value:this.measure(row.inseam)})));
        else if (!primary) body.append(make('span','asf-difference',this.t(row.length<result.best.length?'shorter':'longer',{value:this.measure(Math.abs(row.length-result.best.length))})));
        el.append(size,body);
        const available=this.available(row.size);
        const button=make('button','asf-select',available?this.t('select_size',{size:row.size}):this.t('sold_out'));
        button.type='button';button.dataset.choose=row.size;button.disabled=!available;
        el.append(button);return el;
      };
      panel.append(card(result.best,true));
      if(result.alternative) panel.append(card(result.alternative,false));
      if(!result.smallestRoomy && result.fitLimited) panel.append(make('p','asf-length-note',this.t(result.fitLimited)));
      else if(!result.smallestRoomy && other.best && other.best.size===result.best.size) panel.append(make('p','asf-length-note',this.t('same_size')));
      if(result.lengthWarning) panel.append(make('p','asf-length-note',this.t(result.bottom?'inseam_note':'tall_note')));
      panel.append(make('p','asf-disclaimer',this.t(result.bottom?'bottom_disclaimer':result.measured?'measured_disclaimer':'disclaimer')));
      panel.hidden=false;
      this.querySelector('[data-summary]').textContent=this.t(result.smallestRoomy?'smallest_roomy_summary':'summary',{size:result.best.size});
    }
    choose(size) {
      if (!this.available(size)) {this.calculate();return;}
      const scope=this.closest('.shopify-section') || document;
      const fieldset=scope.querySelector('variant-selector')?.querySelectorAll('fieldset')[this.data.position-1];
      const input=fieldset && [...fieldset.querySelectorAll('input[type="radio"]')].find(x=>x.value===size);
      const select=fieldset?.querySelector('select');
      if(input) {input.checked=true;input.dispatchEvent(new Event('change',{bubbles:true}));}
      else if(select) {select.value=size;select.dispatchEvent(new Event('change',{bubbles:true}));}
      else {this.querySelector('[data-error]').textContent=this.t('manual_select',{size});return;}
      this.querySelector('details').open=false;
      this.querySelector('summary').focus();
    }
  }
  customElements.define('abstract-size-finder',SizeFinder);
})();
